<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import SidebarNavItem from '@/components/layout/sidebar/SidebarNavItem.vue'
import SidebarSectionDivider from '@/components/layout/sidebar/SidebarSectionDivider.vue'

const { t } = useI18n()
const { user } = useAuth()
const emit = defineEmits<{ (e: 'navigate'): void }>()

const organisationSettingsPath = computed<string | undefined>(() => {
  const slug = user.value?.organisations[0]?.slug
  return slug ? `/organizer/organisations/${slug}/settings` : undefined
})

const apiKeysPath = computed<string | undefined>(() => {
  const slug = user.value?.organisations[0]?.slug
  return slug ? `/organizer/organisations/${slug}/api-keys` : undefined
})

const DASHBOARD_ICON = [
  'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 018.25 20.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25A2.25 2.25 0 0113.5 8.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
]

const CALENDAR_ICON = [
  'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
]

const CLIPBOARD_LIST_ICON = [
  'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z',
]

const BANKNOTES_ICON = [
  'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
]

const TROPHY_ICON = [
  'M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0',
]

const ENVELOPE_ICON = [
  'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
]

const USERS_ICON = [
  'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
]

const USER_CIRCLE_ICON = [
  'M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z',
]

const BUILDING_ICON = [
  'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z',
]

const KEY_ICON = [
  'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z',
]
</script>

<template>
  <nav class="flex flex-col py-3" aria-label="Organisation navigation">
    <SidebarNavItem
      to="/organizer/dashboard"
      :label="t('nav.sidebar.dashboard')"
      :exact-active="true"
      :icon="DASHBOARD_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      to="/organizer/events"
      :label="t('nav.sidebar.events')"
      :icon="CALENDAR_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      to="/organizer/wizard-forms"
      :label="t('nav.sidebar.wizard_forms')"
      :icon="CLIPBOARD_LIST_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      to="/organizer/result-formats"
      :label="t('nav.sidebar.result_formats')"
      :icon="TROPHY_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      to="/organizer/finance"
      :label="t('nav.sidebar.finance')"
      :icon="BANKNOTES_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      to="/organizer/newsletters"
      :label="t('nav.sidebar.newsletters')"
      :icon="ENVELOPE_ICON"
      @navigate="emit('navigate')"
    />

    <SidebarSectionDivider />

    <SidebarNavItem
      to="/organizer/team"
      :label="t('nav.sidebar.team')"
      :icon="USERS_ICON"
      @navigate="emit('navigate')"
    />

    <SidebarSectionDivider />

    <SidebarNavItem
      to="/organizer/profile"
      :label="t('nav.sidebar.profile')"
      :icon="USER_CIRCLE_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      v-if="organisationSettingsPath"
      :to="organisationSettingsPath"
      :label="t('nav.sidebar.organisation')"
      :icon="BUILDING_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      v-else
      :label="t('nav.sidebar.organisation')"
      :icon="BUILDING_ICON"
      :disabled="true"
    />
    <SidebarNavItem
      v-if="apiKeysPath"
      :to="apiKeysPath"
      :label="t('nav.sidebar.api_keys')"
      :icon="KEY_ICON"
      @navigate="emit('navigate')"
    />
    <SidebarNavItem
      v-else
      :label="t('nav.sidebar.api_keys')"
      :icon="KEY_ICON"
      :disabled="true"
    />
  </nav>
</template>
